import { OctagonAlertIcon, Redo2, Undo2 } from "lucide-react";

import { Button } from "@design-lab/shared";
import { track } from "jiffy-analytics";
import { useTokenManagement } from "../../model/tokenManagement";
import { GenerationNotification } from "./GenerationNotification";
import { HelpTooltip } from "./HelpTooltip";
import { CreditsHeader, getGenerateButtonContent } from "./shared";

export type PromptFormProps = {
  prompt: string;
  onPromptChange: (prompt: string) => void;
  onGenerate: (prompt: string) => void;
  isLoading?: boolean;
  error?: string | undefined;
  hasGeneratedImages?: boolean;
  isRefineMode?: boolean;
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
  showCredits?: boolean;
};

export function PromptForm({
  prompt,
  onPromptChange,
  onGenerate,
  isLoading = false,
  error = undefined,
  hasGeneratedImages = false,
  isRefineMode = false,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
  showCredits = false
}: PromptFormProps) {
  const { aiCreditsBalance } = useTokenManagement();
  const hasCredits = aiCreditsBalance > 0;

  const handleGenerate = () => {
    if (prompt.trim()) {
      onGenerate(prompt);
    }
  };

  const handleFocus = () => {
    track("weave_generate_input_focused");
  };

  return (
    <div className="flex flex-1 flex-col">
      {/* Prompt Label */}
      {showCredits ? (
        <CreditsHeader labelHtmlFor="prompt" helpTestId="prompt-help-trigger" />
      ) : (
        <div className="mb-1 flex items-center justify-between">
          <label
            htmlFor="prompt"
            className="text-sm font-semibold leading-5 text-gray-700"
            data-testid="prompt-label"
          >
            Your prompt
          </label>
          <HelpTooltip />
        </div>
      )}

      {/* Text Area */}
      <textarea
        id="prompt"
        value={prompt}
        onChange={e => onPromptChange(e.target.value)}
        onFocus={handleFocus}
        placeholder="e.g., A majestic lion wearing a crown, vintage comic book style"
        className="mb-2 resize-none rounded-lg border border-gray-300 p-2 text-sm font-normal leading-5 placeholder-gray-400 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
        rows={3}
        data-testid="prompt-textarea"
        autoFocus
      />

      <div className="flex w-full items-center gap-2">
        {isRefineMode && onUndo && onRedo && (
          <div className="flex gap-2">
            <Button
              onPress={onUndo}
              isDisabled={!canUndo || isLoading}
              className="box-border flex h-11 items-center justify-center rounded-lg border border-blue-700 bg-white px-3 py-2.5 text-base leading-6 text-blue-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-transparent disabled:bg-gray-100 disabled:text-gray-400 disabled:opacity-50"
              data-testid="undo-button"
              aria-label="Undo (Cmd/Ctrl + Z)"
            >
              <Undo2 className="size-5" />
            </Button>
            <Button
              onPress={onRedo}
              isDisabled={!canRedo || isLoading}
              className="box-border flex h-11 items-center justify-center rounded-lg border border-blue-700 bg-white px-3 py-2.5 text-base leading-6 text-blue-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-transparent disabled:bg-gray-100 disabled:text-gray-400 disabled:opacity-50"
              data-testid="redo-button"
              aria-label="Redo (Cmd/Ctrl + Shift + Z)"
            >
              <Redo2 className="size-5" />
            </Button>
          </div>
        )}

        {/* Generate Button */}
        <Button
          onPress={handleGenerate}
          isDisabled={!prompt.trim() || isLoading || !hasCredits}
          className="bg-cta-gradient hover:bg-cta-gradient-hover flex h-11 flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-center text-base font-semibold leading-6 text-white transition-all disabled:cursor-not-allowed disabled:opacity-70"
          data-testid="generate-button"
        >
          {getGenerateButtonContent({
            isLoading,
            hasCredits,
            actionText: isRefineMode ? "Edit Artwork" : "Generate",
            loadingText: isRefineMode ? "Thinking..." : "Generating..."
          })}
        </Button>
      </div>

      {/* Message Block */}
      {(error || !hasCredits) && (
        <div className="mt-2 flex items-center gap-3 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2">
          <OctagonAlertIcon className="size-6 text-orange-600" />
          <p className="text-sm font-normal leading-[18px] text-gray-700">
            {error ||
              "You've used all your daily credits. More credits will be available tomorrow."}
          </p>
        </div>
      )}

      {/* Generation Notification */}
      {hasGeneratedImages && !error && hasCredits && <GenerationNotification />}
    </div>
  );
}
